import React, { useState } from 'react';

const VideoLibrary = ({ videos, loading, onDeleteVideo }) => {
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [showThreadModal, setShowThreadModal] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);

  const handleDelete = async (video) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      setDeletingVideo(video.id);
      try {
        await onDeleteVideo(video.id, video.videoUrl);
      } catch (error) {
        console.error('Failed to delete video:', error);
      } finally {
        setDeletingVideo(null);
      }
    }
  };

  const downloadVideo = (video) => {
    const link = document.createElement('a');
    link.href = video.videoUrl;
    link.download = `twizzler-${video.title || 'video'}-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Thread functionality
  const openThreadModal = (video) => {
    if (video.tweetData?.isThread && video.tweetData?.threadData) {
      setSelectedThread(video.tweetData.threadData);
      setShowThreadModal(true);
    }
  };

  const closeThreadModal = () => {
    setShowThreadModal(false);
    setSelectedThread(null);
  };

  const copyThreadText = () => {
    if (!selectedThread?.tweets) return;
    
    // Helper function to remove links from text and format it correctly
    const formatTweetText = (text) => {
      let processedMessage = text;
      
      // Remove http/https links
      processedMessage = processedMessage.replace(/https?:\/\/[^\s]+/g, '');
      // Remove t.co links
      processedMessage = processedMessage.replace(/t\.co\/[^\s]+/g, '');
      // Remove any remaining URLs
      processedMessage = processedMessage.replace(/[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}/g, '');

      // Enhanced preprocessing for Twitter API text formatting (from ThreadGenerator.js's renderMessage)
      processedMessage = processedMessage.replace(/\r\n/g, '\n'); // Windows line breaks
      processedMessage = processedMessage.replace(/\r/g, '\n');   // Mac line breaks
      
      // Handle numbered list patterns (common in threads)
      processedMessage = processedMessage.replace(/(\d+\.\s)/g, '\n$1');
      
      // Handle bullet point patterns
      processedMessage = processedMessage.replace(/([•·▪▫‣⁃]\s)/g, '\n$1');
      
      // Handle paragraph breaks (double spaces often indicate intended line breaks)
      processedMessage = processedMessage.replace(/\s{2,}/g, '\n');
      
      // Clean up multiple consecutive newlines
      processedMessage = processedMessage.replace(/\n{3,}/g, '\n\n');
      
      // Trim leading/trailing whitespace and newlines
      return processedMessage.trim();
    };
    
    let threadText = '';
    selectedThread.tweets.forEach((tweet, index) => {
      // Clean and format the tweet text
      const cleanedText = formatTweetText(tweet.text);
      threadText += cleanedText;
      
      // Add 2 line breaks between tweets (except for the last one)
      if (index < selectedThread.tweets.length - 1) {
        threadText += '\n\n';
      }
    });

    navigator.clipboard.writeText(threadText).then(() => {
      // Show success feedback
      const button = document.querySelector('.copy-thread-text-btn');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✅ Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    }).catch(() => {
      alert('Failed to copy thread text');
    });
  };

  const countThreadVideos = () => {
    return videos.filter(video => video.tweetData?.isThread).length;
  };

  if (loading) {
    return (
      <div className="library-loading">
        <div className="loading-spinner-large"></div>
        <p>Loading your videos...</p>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="library-empty">
        <div className="empty-icon">📹</div>
        <h3>No videos yet</h3>
        <p>Create your first viral tweet video to see it here</p>
      </div>
    );
  }

  return (
    <div className="video-library">
      <div className="library-stats">
        <div className="stat-item">
          <span className="stat-number">{videos.length}</span>
          <span className="stat-label">Total Videos</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{countThreadVideos()}</span>
          <span className="stat-label">Thread Videos</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{videos.filter(v => v.downloadCount > 0).length}</span>
          <span className="stat-label">Downloaded</span>
        </div>
      </div>

      <div className="video-grid">
        {videos.map((video) => (
          <div key={video.id} className="video-card">
            <div className="video-preview">
              {video.thumbnailUrl ? (
                <img 
                  src={video.thumbnailUrl} 
                  alt="Video thumbnail"
                  className="video-thumbnail"
                />
              ) : (
                <div className="video-placeholder">
                  <span className="placeholder-icon">🎬</span>
                </div>
              )}
              <div className="video-overlay">
                <button 
                  className="play-btn"
                  onClick={() => window.open(video.videoUrl, '_blank')}
                >
                  ▶
                </button>
              </div>
            </div>

            <div className="video-info">
              <div className="video-header">
                <h4 className="video-title">
                  {video.title || 'Untitled Video'}
                </h4>
                {video.tweetData?.isThread && (
                  <div className="thread-badge">
                    🧵 Thread
                  </div>
                )}
              </div>
              <p className="video-date">
                {formatDate(video.createdAt)}
              </p>
              
              {video.tweetData && (
                <div className="tweet-info">
                  <p className="tweet-author">@{video.tweetData.username}</p>
                  <p className="tweet-text">
                    {video.tweetData.message.slice(0, 80)}
                    {video.tweetData.message.length > 80 ? '...' : ''}
                  </p>
                  {video.tweetData.isThread && (
                    <div className="thread-info">
                      {video.tweetData.threadData?.tweets?.length} tweets in thread
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="video-actions">
              <button 
                className="action-btn download-btn"
                onClick={() => downloadVideo(video)}
                title={video.tweetData?.isThread ? "Download Video" : undefined}
              >
                <span className="btn-icon">📥</span>
                {!video.tweetData?.isThread && "Download"}
              </button>
              {video.tweetData?.isThread && (
                <button 
                  className="action-btn thread-btn"
                  onClick={() => openThreadModal(video)}
                >
                  <span className="btn-icon">🧵</span>
                  View Thread
                </button>
              )}
              <button 
                className="action-btn delete-btn"
                onClick={() => handleDelete(video)}
                disabled={deletingVideo === video.id}
              >
                <span className="btn-icon">
                  {deletingVideo === video.id ? '⏳' : '🗑️'}
                </span>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Thread Modal */}
      {showThreadModal && selectedThread && (
        <div className="thread-modal-overlay" onClick={closeThreadModal}>
          <div className="thread-modal" onClick={(e) => e.stopPropagation()}>
            <div className="thread-modal-header">
              <h3>🧵 Thread Content</h3>
              <button className="modal-close-btn" onClick={closeThreadModal}>
                ×
              </button>
            </div>
            
            <div className="thread-modal-body">
              <div className="thread-modal-info">
                <div className="thread-modal-stats">
                  <div className="modal-stat">
                    <span className="modal-stat-number">{selectedThread.tweets.length}</span>
                    <span className="modal-stat-label">tweets</span>
                  </div>
                  <div className="modal-stat">
                    <span className="modal-stat-number">{selectedThread.threadStats.totalLikes.toLocaleString()}</span>
                    <span className="modal-stat-label">likes</span>
                  </div>
                  <div className="modal-stat">
                    <span className="modal-stat-number">{selectedThread.threadStats.totalRetweets.toLocaleString()}</span>
                    <span className="modal-stat-label">retweets</span>
                  </div>
                </div>
              </div>

              <div className="thread-modal-content">
                <h4 className="thread-modal-title">Thread Preview</h4>
                <div className="thread-modal-tweets">
                  {selectedThread.tweets.map((tweet, index) => (
                    <div key={tweet.id} className="thread-modal-tweet">
                      <div className="tweet-modal-number">{index + 1}/{selectedThread.tweets.length}</div>
                      <div className="tweet-modal-text">{tweet.text}</div>
                      <div className="tweet-modal-metrics">
                        <span>❤️ {tweet.metrics.likeCount.toLocaleString()}</span>
                        <span>🔄 {tweet.metrics.retweetCount.toLocaleString()}</span>
                        <span>💬 {tweet.metrics.replyCount.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="thread-modal-actions">
              <button 
                className="copy-thread-text-btn"
                onClick={copyThreadText}
              >
                📋 Copy Thread Text
              </button>
              <button 
                className="modal-close-action-btn"
                onClick={closeThreadModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLibrary; 